import React, { createContext, useContext, useEffect, useReducer } from "react";
import browserStorage from "store";

// Create the Cart context
const CartContext = createContext();

const itemExists = (cartItems, newItem) => {
  return cartItems?.some((item) => item.item_id === newItem.item_id);
};

const getCartItemsQty = (items = []) => {
    const qty = items?.reduce((total, item) => {
      const isFree = item?.extension_attributes?.amasty_promo?.is_free === true
      const q = isFree ? 0 : item?.qty ? item?.qty : item?.ordered_qty ? item?.ordered_qty : 0
      return total = total + q
    }, 0)
    console.log('qty =========>', qty)
    return qty
}

// Cart reducer function
const cartReducer = (state, action) => {
  switch (action.type) {
    case "GET_CART":
      return {
        ...state,
        cart: action?.payload,
        cartItems: action?.payload?.items,
        itemsQty: getCartItemsQty(action?.payload?.items)
      };

    case "LOAD_CART":
      return {
        ...state,
        cart: action.payload?.cart,
        cartItems: action.payload?.cartItems,
        itemsQty: getCartItemsQty(action?.payload?.items),
      };

    case "ADD_ITEMS":
      const items = action.payload;
      const updatedCartItems = state.cartItems?.map((item) => {
        if (itemExists(state.cartItems, item)) {
          const newItem = items?.find(
            (newItem) => newItem.item_id === item.item_id
          );
          return { ...item, quantity: (item.qty = newItem?.qty) };
        }
        return item;
      });

      const newItems = items?.filter(
        (newItem) => !itemExists(state.cartItems, newItem)
      );
      const mergedCartItems = [...updatedCartItems, ...newItems];

      return {
        ...state,
        cartItems: mergedCartItems,
        itemsQty: getCartItemsQty(mergedCartItems),
      };

    case "ADD_ITEM":
      const newItem = action?.payload;
      const itemIndex = state?.cartItems?.findIndex(
        (item) => item.item_id === newItem.item_id
      );

      if (itemIndex >= 0) {
        state.cartItems[itemIndex].qty = newItem?.qty;
        // refresh the qty-matched Extended Warranty slot array from the
        // fresh /cart/add response. Merge into existing extension_attributes so
        // product_fees / amasty_promo already on the line are preserved.
        if (newItem?.extension_attributes?.extended_warrantys) {
          state.cartItems[itemIndex].extension_attributes = {
            ...state.cartItems[itemIndex].extension_attributes,
            extended_warrantys:
              newItem.extension_attributes.extended_warrantys,
          };
        }
      } else {
        state.cartItems?.push(newItem);
      }

      const cartInfoItemIndex = state?.cartInfo?.findIndex(
        (item) => item.item_id === newItem.item_id
      );

      if (cartInfoItemIndex >= 0) {
        state.cartInfo[cartInfoItemIndex].qty = newItem?.qty;
      } else {
        state.cartInfo?.push(newItem);
      }
      return { ...state, itemsQty: getCartItemsQty(state?.cartItems) };

    case "ADD_ITEM_WITH_SOURCE":
      const exists = state.searchSourceItems?.some(
        (item) => item.item_id === action.payload.item_id
      );

      return {
        ...state,
        searchSourceItems: exists
          ? state.searchSourceItems
          : [
              ...(state.searchSourceItems || []),
              {
                item_id: action.payload.item_id,
                queryID: action.payload.query,
                indexName: action.payload.indexName,
                entity_id: action.payload.entity_id,
              },
            ],
      };

    case "UPDATE_ITEM":
      const { item_id, qty } = action.payload;
      return {
        ...state,
        cartItems: state?.cartItems?.map((item) =>
          item.item_id === item_id ? { ...item, qty: qty } : item
        ),
        cartInfo: state?.cartInfo?.map((item) =>
          item.item_id === item_id ? { ...item, qty: qty } : item
        ),
        itemsQty: getCartItemsQty(state?.cartItems),
      };

    case "DELETE_ITEM":
      return {
        ...state,
        cartItems: state?.cartItems?.filter(
          (item) => item.item_id !== action.payload.item_id
        ),
        cartInfo: state?.cartInfo?.filter(
          (item) => item.item_id !== action.payload.item_id
        ),
        searchSourceItems: state?.searchSourceItems?.filter(
          (item) => item.item_id !== action.payload.item_id
        ),
        itemsQty: getCartItemsQty(state?.cartItems),
      };

    case "RESET_CART":
      return {
        cart: {},
        cartItems: [],
        cartInfo: [],
        searchSourceItems: [],
        itemsQty: 0
      };

    default:
      return state;
  }
};

// Cart context provider component
const CartProvider = ({ children }) => {
  const savedSData = browserStorage.get("cart") || {
    cart: {},
    cartItems: [],
    searchSourceItems: [],
    itemsQty: 0
  };
  const [cart, dispatch] = useReducer(cartReducer, {
    ...savedSData,
    cartInfo: [],
  });

  // Save the cart data to localStorage whenever the cart changes
  useEffect(() => {
    browserStorage.set("cart", cart);
  }, [cart]);

  return (
    <CartContext.Provider value={{ cart, dispatch }}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the CartContext
const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// ---- Extended Warranty helpers ----
// Per Magento: each cart item carries extension_attributes.extended_warrantys[].
// Each slot carries a `qty` (how many units this EW covers — shown in the readonly
// badge). Slot shape: { cart_item_id, position, qty, extended_warranty_id?, price_id?,
// prices: [{ price_id, code, year, price, program, is_selected }] }
// A slot is "filled" when price_id at top level is set (or any price has is_selected=true).

const getCartLineSlots = (item) =>
  item?.extension_attributes?.extended_warrantys || [];

const isSlotFilled = (slot) =>
  slot?.price_id !== undefined && slot?.price_id !== null;

const getSelectedPriceForSlot = (slot) =>
  slot?.prices?.find((p) => p?.is_selected) || null;

// Defensive client-side EW total — used only if Magento doesn't emit the
// `extended_warranty` total_segment. Happy path reads from total_segments
// in OrderSummary.js directly.
const getEWTotalForLine = (item) =>
  getCartLineSlots(item).reduce((sum, slot) => {
    const sel = getSelectedPriceForSlot(slot);
    return sum + (sel?.price || 0);
  }, 0);

const getEWTotal = (cartItems = []) =>
  cartItems.reduce((sum, item) => sum + getEWTotalForLine(item), 0);

export {
  CartProvider,
  useCart,
  getCartLineSlots,
  isSlotFilled,
  getSelectedPriceForSlot,
  getEWTotalForLine,
  getEWTotal,
};
